import { action, computed, observable } from 'mobx';
import {
    AttributeValue,
    EventAttributeTypesValues,
    IEventAttributeDetailsDTO,
} from '../../../../../../../../../api/attributes.dao';
import { IEventAttributeValue } from '../../../../../../../../../api/events.dao';

export class ModuleAttributeVM {
    @observable
    private selectedValues: AttributeValue[] = [];

    @observable
    private touched = false;

    private constructor(private readonly attribute: IEventAttributeDetailsDTO) {}

    public static createNew(attribute: IEventAttributeDetailsDTO): ModuleAttributeVM {
        return new ModuleAttributeVM(attribute);
    }

    public static createFromDTO(dto: IEventAttributeValue, attribute: IEventAttributeDetailsDTO): ModuleAttributeVM {
        const vm = new ModuleAttributeVM(attribute);
        vm.initFromDTO(dto);
        return vm;
    }

    @computed
    public get valid(): boolean {
        if (this.isCheckbox()) return true;
        return this.selectedValues.length > 0;
    }

    @computed
    public get sortedValues(): AttributeValue[] {
        return [...this.selectedValues, ...this.unselectedActive()];
    }

    public getAttributeId(): number {
        return this.attribute.eventAttributeId;
    }

    public getAttributeName(): string {
        return this.attribute.eventAttributeName;
    }

    public getInputType(): string {
        return this.attribute.inputType;
    }

    public isTouched(): boolean {
        return this.touched;
    }

    @action
    public toggleValue(value: AttributeValue): void {
        if (this.isValueSelected(value)) {
            this.removeValue(value);
        } else {
            this.selectedValues.push(value);
        }
    }

    @action
    public setTouched(): void {
        this.touched = true;
    }

    public toDTO(): IEventAttributeValue {
        if (this.isCheckbox()) return this.toDTOForCheckbox();
        return this.toDTOForSelect();
    }

    public isValueSelected(value: AttributeValue): boolean {
        return this.selectedValues.some(v => v.valueId === value.valueId);
    }

    private isCheckbox(): boolean {
        return this.attribute.inputType === EventAttributeTypesValues.CHECKBOX;
    }

    private unselectedActive(): AttributeValue[] {
        return (this.attribute.values ?? [])
            .filter(v => v.active && !this.isValueSelected(v))
            .sort((a, b) => a.attributeOrder - b.attributeOrder);
    }

    @action
    private removeValue(value: AttributeValue): void {
        const idx = this.selectedValues.findIndex(v => v.valueId === value.valueId);
        if (idx >= 0) this.selectedValues.splice(idx, 1);
    }

    @action
    private initFromDTO(dto: IEventAttributeValue): void {
        this.selectedValues = ModuleAttributeVM.resolveSelectedValues(dto, this.attribute);
    }

    private toDTOForCheckbox(): IEventAttributeValue {
        return {
            attributeId: this.attribute.eventAttributeId,
            attributeValue: 'true',
        };
    }

    private toDTOForSelect(): IEventAttributeValue {
        return {
            attributeId: this.attribute.eventAttributeId,
            multiAttributeValues: this.selectedValues.map(v => String(v.valueId)),
        };
    }

    private static resolveSelectedValues(dto: IEventAttributeValue, attribute: IEventAttributeDetailsDTO): AttributeValue[] {
        if (attribute.inputType === EventAttributeTypesValues.CHECKBOX) return [];
        return ModuleAttributeVM.resolveMultiValues(dto.multiAttributeValues ?? [], attribute);
    }

    private static resolveMultiValues(valueIds: string[], attribute: IEventAttributeDetailsDTO): AttributeValue[] {
        return valueIds
            .map(id => attribute.values?.find(v => String(v.valueId) === id))
            .filter((v): v is AttributeValue => v !== undefined);
    }
}
